<template>
<div class="flow-analysis-map">
  <el-row type="flex" justify="space-between">
    <el-input class="search" style="width: 200px" v-model="searchValue" size="mini" placeholder="IP 주소를 입력하세요." @keyup.enter.native="search">
      <!-- <i slot="suffix" class="c-pointer el-input__icon el-icon-search" @click="search"></i> -->
    </el-input>
    <div>
      <ul class="diff-legend-box mr-r4">
        <li><span class="item bg-src"></span>Src</li>
        <li><span class="item bg-dst"></span>Dst</li>
        <li><span class="item bg-both"></span>Both</li>
      </ul>
      <el-popover placement="bottom" width="400" trigger="hover">
        <div>
          <div>
            마우스 커서가 위치한 IP의 상대방 IP 색상 범례입니다.<br /> (ex) 마우스 커서가 위치한 IP 0.0.0.0
          </div>
          <div style="display:inline-block">
            <div> 0.0.0.0 [Src] →
              <span class="color-dst">1.1.1.1 [Dst]</span>
            </div>
            <div> 0.0.0.0 [Dst] ←
              <span class="color-src">1.1.1.1 [Src]</span>
            </div>
            <div> 0.0.0.0 [Both] ↔
              <span class="color-both">1.1.1.1 [Both]</span>
            </div>
          </div>
        </div>
        <el-button slot="reference" size="mini"><i class="fas fa-question"></i></el-button>
      </el-popover>
    </div>
  </el-row>
  <div class="flowmap-cls"></div>
  <el-row type="flex" justify="center">
    <div class="flow-line-box">
      <div class="line first"/>
      <div class="label">1G 이상</div>
    </div>
    <div class="flow-line-box">
      <div class="line second"/>
      <div class="label">999.0M ~ 500.0M</div>
    </div>
  </el-row>
  <el-row type="flex" justify="center">
    <div class="flow-line-box">
      <div class="line third"/>
      <div class="label">4990M ~ 100.0M</div>
    </div>
    <div class="flow-line-box">
      <div class="line default"/>
      <div class="label">100.0M 이하</div>
    </div>
  </el-row>
</div>
</template>

<script>
import * as d3 from 'd3';
import isEmpty from 'lodash/isEmpty';
import MsgUtil from '@/utils/message';

const lineColor = {
  FIRST: '#d2767c',
  SECOND: '#e3b88c',
  THIRD: '#7ebf89',
  DEFAULT: '#83b2dc',
};

export default {
  name: 'WFlowMapComponent',
  data() {
    return {
      mapData: null,
      searchValue: '',
    };
  },
  methods: {
    /**
     * 부모(FlowMapAnalysisPopup) 팝업 종료시 flow map 초기화
     * svg 영역 제거, 검색창 초기화
     * @author jeee12
     */
    initFlowMap() {
      this.searchValue = '';
      d3
        .select('.flowmap-cls')
        .select('svg')
        .remove();
    },
    /**
     * 트래픽 맵 생성
     * @author jeee12
     */
    d3DrawMap(trafficData) {
      const diameter = document.body.clientHeight - 410;
      const radius = diameter / 2;
      const innerRadius = radius - 100;
      const cluster = d3.cluster().size([360, innerRadius]);
      const line = d3
        .radialLine()
        .curve(d3.curveBundle.beta(0.85))
        .radius((d) => d.y)
        .angle((d) => (d.x / 180) * Math.PI);

      this.initFlowMap();

      const svg = d3
        .select('.flowmap-cls')
        .append('svg')
        .attr('width', diameter)
        .attr('height', diameter)
        .attr('style', 'overflow:inherit')
        .append('g')
        .attr('transform', `translate(${radius + 150},${radius - 40})`);

      svg
        .append('g')
        .append('circle')
        .attr('r', innerRadius)
        .attr('class', 'flowmap-circle-line');
      let link = svg.append('g').selectAll('.flowmap-link');
      let node = svg.append('g').selectAll('.flowmap-node');

      const classes = trafficData;
      const pack = d3.hierarchy(this.packageHierarchy(classes), (d) => d.children);

      cluster(pack);

      const links = this.packageImports(pack.descendants());
      const nodes = pack.descendants();

      link = link
        .data(links)
        .enter()
        .append('path')
        .attr('class', 'flowmap-link')
        .attr('d', (d) => line(d.source.path(d.target)))
        .style('stroke', (d) => {
          // 트래픽양에 따른 색상 표현 (임의 색상 지정)
          const traffic = d.target.data.trafficMap[d.source.data.name];
          if (traffic < 100 * (1024 ** 2)) { // 100.0M
            return lineColor.DEFAULT;
          } else if (traffic >= 100 * (1024 ** 2) && traffic < 500 * (1024 ** 2)) { // 499.0M ~ 100.0M
            return lineColor.THIRD;
          } else if (traffic >= 500 * (1024 ** 2) && traffic < 1024 ** 3) { // 999.0M ~ 500.0M
            return lineColor.SECOND;
          } else if (traffic >= 1024 ** 3) { // 1G
            return lineColor.FIRST;
          }
          return '';
        });

      node = node
        .data(nodes.filter((n) => !n.children))
        .enter()
        .append('text')
        .attr('class', 'flowmap-node')
        .attr('dy', '.31em')
        .attr('transform', (d) => `rotate(${d.x - 90})translate(${d.y + 8},0)${d.x < 180 ? '' : 'rotate(180)'}`)
        .style('text-anchor', (d) => `${d.x < 180 ? 'start' : 'end'}`)
        .text((d) => d.data.key)
        .on('mouseover', this.mouseovered)
        .on('mouseout', this.mouseouted)
        .on('click', this.mouseclicked);

      this.mapData = {
        link,
        node,
      };
    },
    /**
     * 노드 mouse over
     * @author jeee12
     * @param  {[type]} d [description]
     */
    mouseovered(d) {
      this.mapData.node.each((n) => {
        n.target = false;
        n.source = false;
      });
      /* eslint-disable */
      this.mapData.link
        .classed('flowmap-link--target', function(l) {
          if (l.target === d) return (l.source.source = true);
        })
        .classed('flowmap-link--source', function(l) {
          if (l.source === d) return (l.target.target = true);
        })
        .filter((l) => l.target === d || l.source === d)
        .each(function append() {
          this.parentNode.appendChild(this);
        });
        /* eslint-enable */
      this.mapData.node.classed('flowmap-node--target', (n) => n.target).classed('flowmap-node--source', (n) => n.source);
    },
    /**
     * 노드 mouse out
     * @author jeee12
     */
    mouseouted() {
      this.mapData.link.classed('flowmap-link--target', false).classed('flowmap-link--source', false);

      this.mapData.node.classed('flowmap-node--target', false).classed('flowmap-node--source', false);
    },
    /**
     * 노드 click
     * @author jeee12
     */
    mouseclicked(d) {
      this.$emit('node-click', d.data);
    },
    /**
     * 트래픽 데이터 플로우맵 node hierarchy형식으로 가공
     * @author jeee12
     * @param  {[type]} classes [description]
     * @return {[type]}         [description]
     */
    packageHierarchy(classes) {
      const map = {};

      const find = (name, data) => {
        let node = map[name];
        if (!node) {
          map[name] = data || {
            name,
            children: [],
          };
          node = map[name];
          if (name.length) {
            node.parent = find('');
            node.parent.children.push(node);
            node.key = name;
          }
        }
        return node;
      };

      classes.forEach((d) => {
        find(d.name, d);
      });

      return map[''];
    },
    /**
     * 트래픽 데이터 src, dst 배열로 가공
     * @author jeee12
     * @param  {[type]} classes [description]
     * @return {[type]}         [description]
     */
    packageImports(nodes) {
      const map = {};
      const imports = [];

      // Compute a map from name to node.
      nodes.forEach((d) => {
        map[d.data.name] = d;
      });

      // For each import, construct a link from the source to target node.
      nodes.forEach((d) => {
        if (d.data.conversationList) {
          d.data.conversationList.forEach((i) => {
            const target = map[i.name]; // dst 노드에 src IP주소와 트래픽 양을 맵으로 저장
            if (!target.data.trafficMap) target.data.trafficMap = {};
            target.data.trafficMap[d.data.name] = i.value;
            imports.push({
              source: map[d.data.name],
              target,
            });
          });
        }
      });
      return imports;
    },
    /**
     * IP 주소 검색
     * @author jeee12
     */
    search() {
      const mapData = this.mapData;
      if (!isEmpty(mapData)) {
        const link = mapData.link;
        const node = mapData.node;
        const ipaddress = this.searchValue;

        if (isEmpty(ipaddress) || ipaddress === '') {
          link.style('opacity', 1);
          node
            .style('opacity', 1)
            .style('cursor', 'pointer')
            .on('click', this.mouseclicked);
        } else {
          link.style('opacity', 0);
          node
            .each((n) => {
              n.showFlag = false;
            })
            .style('opacity', 0)
            .style('cursor', 'defaults')
            .on('click', false);

          let index = 0;
          link
            .filter((l) => {
              let flag = false;
              if (l.source.data.name.indexOf(ipaddress) !== -1 || l.target.data.name.indexOf(ipaddress) !== -1) {
                l.source.showFlag = true;
                l.target.showFlag = true;
                flag = true;
                index += 1;
              }
              return flag;
            })
            .style('opacity', 1);

          node
            .filter((n) => n.showFlag)
            .style('opacity', 1)
            .style('cursor', 'pointer')
            .on('click', this.mouseclicked);

          if (index < 1) {
            this.$message(MsgUtil.getCustomResultMsgData('검색 조건에 일치하는 IP가 없습니다.', false));
          }
        }
      }
    },
  },
};
</script>

<style lang="scss">
.flow-analysis-map {
  $color-src: #5187d1;
  $color-dst: #90ce6b;
  $color-both: #ff7689;

  $line-first: #d2767c;
  $line-second: #e3b88c;
  $line-third: #7ebf89;
  $line-default: #83b2dc;

  .color {
    &-src {
      color: $color-src;
    }

    &-dst {
      color: $color-dst;
    }

    &-both {
      color: $color-both;
    }
  }

  .bg {
    &-src {
      background: $color-src;
    }

    &-dst {
      background: $color-dst;
    }

    &-both {
      background: $color-both;
    }
  }

  .flow-line-box {
    display: inline-block;
    width: 205px;

    .line {
      display: inline-block;
      width: 45px;
      height: 3px;
      margin-bottom: 2px;
      margin-right: 4px;

      &.first {
        background: $line-first;
      }

      &.second {
        background: $line-second;
      }

      &.third {
        background: $line-third;
      }

      &.default {
        background: $line-default;
      }
    }

    .label {
      display: inline-block;
    }
  }

  .flowmap-cls {
    height: 500px;
  }

  .flowmap-circle-line {
    fill: transparent;
    stroke: #69778c;
    stroke-width: 2px;
  }

  .flowmap-node {
    font: 600 11px 'Nanum Gothic', sans-serif;
    fill: #9395a8;
    cursor: pointer;
  }

  .flowmap-node:hover {
    fill: #000000;
  }

  .flowmap-link {
    stroke: steelblue;
    stroke-opacity: 0.4;
    fill: none;
    pointer-events: none;
  }

  .flowmap-node--source,
  .flowmap-node--target,
  .flowmap-node:hover {
    font-weight: 700;
  }

  .flowmap-node--source {
    /* Source IP */
    fill: $color-src;
  }

  .flowmap-node--target {
    /* Destination IP */
    fill: $color-dst;
  }

  .flowmap-node--source.flowmap-node--target {
    fill: $color-both;
  }

  .flowmap-link--source,
  .flowmap-link--target {
    stroke-opacity: 1;
    stroke-width: 2px;
  }

  .flowmap-link--source {
    /*stroke: #3494FE;*/
    stroke: #5fa7ec;
  }

  .flowmap-link--target {
    /*stroke: #3494FE;*/
    stroke: #5fa7ec;
  }

  .flowmap-link--source.flowmap-link--target {
    stroke: blue;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/performance/FlowAnalysis/components/popup/FlowMapComponent.vue